import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApproveReq,
  BuyReq,
  GetClaimReq,
  GetKycReq,
  GetTokenBalanceReq,
  SaveKycReq,
  SwapReq,
} from 'entities/user/model';
import { RequestWithWeb3Provider, toLowerCaseKeys } from 'shared';
import { Chains, ChainType, WalletProviders } from 'shared/config/wallet-service';

type TokenInfo = {
  balance: string;
  decimals: string;
  symbol: string;
};

export type UserState = {
  address: string;
  provider: WalletProviders;
  chainType: ChainType;
  network: Chains;
  isKycApproved: boolean;
  tokens: Record<string, TokenInfo>;
  vestingSwapInfo: {
    finish: number;
    vestingRates: [number, number][];
    vestingRewards: [number, string][];
    availableToClaimSum: number;
    startTime: number;
    swapRateDenominator: number;
  };
  buyInfo: {
    price: string;
    nativePrice: string;
    tokensLeftOnContract: string;
  };
  airdropInfo: { totalAmount: number; merkleProof: string; withdrawn: boolean };
};

const initialUserState: UserState = {
  address: '',
  provider: WalletProviders.metamask,
  chainType: process.env.REACT_APP_CHAIN_TYPE as ChainType,
  network: Chains.Ethereum,
  isKycApproved: false,
  tokens: {},
  vestingSwapInfo: {
    finish: 0,
    vestingRates: [],
    vestingRewards: [],
    availableToClaimSum: 0,
    startTime: 0,
    swapRateDenominator: 3000,
  },
  buyInfo: { price: '0', nativePrice: '0', tokensLeftOnContract: '0' },
  airdropInfo: { totalAmount: 0, merkleProof: '', withdrawn: false },
};

const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    updateUserState: (state: UserState, action: PayloadAction<Partial<UserState>>) => ({
      ...state,
      ...action.payload,
    }),

    clearUserState: () => ({
      ...initialUserState,
    }),

    updateUserTokensBalances: (state, action: PayloadAction<UserState['tokens']['balances']>) => {
      const lowerCasedKeysMap = toLowerCaseKeys(action.payload);
      return {
        ...state,
        tokens: {
          ...state.tokens,
          balances: {
            ...state.tokens.balances,
            ...lowerCasedKeysMap,
          },
        },
      };
    },

    updateUserTokensDecimals: (state, action: PayloadAction<UserState['tokens']['decimals']>) => {
      const lowerCasedKeysMap = toLowerCaseKeys(action.payload);
      return {
        ...state,
        tokens: {
          ...state.tokens,
          decimals: {
            ...state.tokens.decimals,
            ...lowerCasedKeysMap,
          },
        },
      };
    },

    updateUserTokens: (state, action: PayloadAction<UserState['tokens']>) => {
      const lowerCasedKeysMap = toLowerCaseKeys(action.payload);
      return {
        ...state,
        tokens: {
          ...state.tokens,
          ...lowerCasedKeysMap,
        },
      };
    },

    updateVestingSwapInfo: (state, action: PayloadAction<UserState['vestingSwapInfo']>) => {
      return {
        ...state,
        vestingSwapInfo: {
          ...state.vestingSwapInfo,
          ...action.payload,
        },
      };
    },

    updateBuyInfo: (state, action: PayloadAction<UserState['buyInfo']>) => {
      return {
        ...state,
        buyInfo: {
          ...state.buyInfo,
          ...action.payload,
        },
      };
    },

    updateAirdropInfo: (state, action: PayloadAction<Partial<UserState['airdropInfo']>>) => {
      return {
        ...state,
        airdropInfo: {
          ...state.airdropInfo,
          ...action.payload,
        },
      };
    },

    disconnectWalletState: (state, action: PayloadAction<{ withoutAuth: boolean } | undefined>) => {
      localStorage.removeItem('walletconnect');
      return {
        ...initialUserState,
        address: action.payload?.withoutAuth ? state.address : initialUserState.address,
        isKycApproved: action.payload?.withoutAuth ? state.isKycApproved : initialUserState.isKycApproved,
      };
    },

    /** For saga */
    /* eslint-disable @typescript-eslint/no-unused-vars */
    /* eslint-disable @typescript-eslint/no-empty-function */
    getIsKycApproved(state, action: PayloadAction<GetKycReq>) {},
    saveKyc(state, action: PayloadAction<SaveKycReq>) {},
    getTokenBalance(state, action: PayloadAction<GetTokenBalanceReq>) {},
    getVestingSwapInfo(state, action: PayloadAction<RequestWithWeb3Provider>) {},
    getAirdropInfo(state, action: PayloadAction<GetClaimReq>) {},
    getBuyInfo(state, action: PayloadAction<RequestWithWeb3Provider>) {},
    approve(state, action: PayloadAction<ApproveReq>) {},
    swap(state, action: PayloadAction<SwapReq>) {},
    claim(state, action: PayloadAction<RequestWithWeb3Provider>) {},
    buy(state, action: PayloadAction<BuyReq>) {},
  },
});

export const { reducer } = userSlice;
export const { actions: userActions } = userSlice;
